import { useCallback, useEffect, useMemo, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { ROUTES } from "@/helpers/routes"
import { useModals } from "@/modals"
import {
  useGetCardBackActiveByPlayerIdQuery,
  useGetCardBackActiveQuery
} from "@/store/apis/cardBacksAPI"
import { useGetDeckHandQuery } from "@/store/apis/deckAPI"
import { EffectTarget, useGetActiveEffectsQuery } from "@/store/apis/effectsAPI"
import {
  IGameplayCardAction,
  IGameplayStatePlayer,
  IGameplayStateResponse,
  useGetGameplayLastLogsQuery,
  useGetGameplayStateQuery,
  useMakeGameplayActionMutation
} from "@/store/apis/gameplayAPI"
import { useGetMatchSessionQuery } from "@/store/apis/matchAPI"
import {
  useGetPlayerInfoQuery,
  useGetPlayerProfileQuery,
  useGetPlayerStatisticsQuery
} from "@/store/apis/playerAPI"
import { useAppDispatch, useAppSelector } from "@/store/index"
import {
  GameplaySelectors,
  setGameDerivedState,
  setSessionId
} from "@/store/slices/gameplay"

export const useGameLogic = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { sessionId: sessionIdParam } = useParams<"sessionId">()
  const { closeAllModals } = useModals()

  const sessionIdTgParam = window.location.search.split("match_")[1]

  const events = useAppSelector(GameplaySelectors.selectEvents())
  const gamePrevState = useAppSelector(GameplaySelectors.selectPrevState())
  const isEventsFulfilled = useAppSelector(
    GameplaySelectors.selectEventFulfilled()
  )
  const sessionIdStore = useAppSelector(GameplaySelectors.selectSessionId())

  const sessionId = useMemo(() => {
    return sessionIdTgParam || sessionIdParam || sessionIdStore
  }, [sessionIdTgParam, sessionIdParam, sessionIdStore])

  const refGameState = useRef<IGameplayStateResponse | undefined>(undefined)
  const refIsMyTurn = useRef<boolean>(false)
  const refIsMatchActive = useRef<boolean>(false)
  const refPlayer = useRef<IGameplayStatePlayer | undefined>(undefined)
  const refOpponent = useRef<IGameplayStatePlayer | undefined>(undefined)

  const { data: playerProfile } = useGetPlayerProfileQuery()

  const { data: matchSession, isLoading: isMatchSessionLoading } =
    useGetMatchSessionQuery(
      { sessionId: sessionId ?? "", playerId: playerProfile?._id ?? "" },
      {
        skip: !sessionId || !playerProfile?._id
      }
    )

  const { data: activeCardBack } = useGetCardBackActiveQuery()

  const isMatchPending = "PENDING" === (matchSession?.status ?? "")
  const isMatchActive = "ACTIVE" === (matchSession?.status ?? "")
  const isMatchFinished = "FINISHED" === (matchSession?.status ?? "")
  const isTonMatch = (matchSession?.pot?.totalPot || 0) > 0

  const {
    data: gameStateData,
    isLoading: isGameStateLoading,
    isFetching: isGameStateFetching
  } = useGetGameplayStateQuery(
    { sessionId: sessionId ?? "" },
    {
      skip: !isMatchActive
    }
  )

  const checkWinGame = (playerId?: string) => {
    const targetId = playerId ?? player?.player
    if (!targetId) return false

    if (matchSession?.winner) {
      return matchSession.winner === targetId
    }

    if (isMe(targetId)) return player?.progress === 0
    return opponent?.progress === 0
  }

  const { data: activeEffectsData, isLoading: isActiveEffectsLoading } =
    useGetActiveEffectsQuery(sessionId ?? "", {
      skip: !isMatchActive
    })

  const { data: playerCardsData, isFetching: isPlayerCardsDataLoading } =
    useGetDeckHandQuery(sessionId ?? "", {
      skip: !isMatchActive
    })

  const { data: lastLogs, isLoading: isLastLogsLoading } =
    useGetGameplayLastLogsQuery(
      { sessionId: sessionId ?? "", count: 30 },
      {
        skip: !isMatchActive
      }
    )

  const isPlayerCardsLoading = useMemo(() => {
    return isMatchActive ? isPlayerCardsDataLoading : isMatchSessionLoading
  }, [isMatchActive, isPlayerCardsDataLoading, isMatchSessionLoading])

  const gameState = useMemo(() => {
    return isMatchActive ? gameStateData : matchSession?.snapshot?.state
  }, [isMatchActive, matchSession, gameStateData])

  const activeEffects = useMemo(() => {
    return isMatchActive
      ? activeEffectsData
      : matchSession?.snapshot?.activeEffects
  }, [isMatchActive, matchSession, activeEffectsData])

  const cards = useMemo(() => {
    return isMatchActive ? playerCardsData : matchSession?.snapshot?.hands
  }, [isMatchActive, matchSession, playerCardsData])

  const [makeGameplayAction] = useMakeGameplayActionMutation()

  const makeGameplayActionCallback = useCallback(
    async (
      cardId: string | null,
      action: IGameplayCardAction
    ): Promise<boolean> => {
      if (!sessionId) return false

      try {
        await makeGameplayAction({ sessionId, cardId, action }).unwrap()
        return true
      } catch {
        return false
      }
    },
    [makeGameplayAction, sessionId]
  )

  const handleDiscardCard = useCallback(
    (cardId?: string | null): Promise<boolean> => {
      if (!cardId) return Promise.resolve(false)
      return makeGameplayActionCallback(cardId, "DISCARD")
    },
    [makeGameplayActionCallback]
  )

  const handlePlayCard = useCallback(
    (cardId?: string | null): Promise<boolean> => {
      if (!cardId) return Promise.resolve(false)
      return makeGameplayActionCallback(cardId, "PLAY")
    },
    [makeGameplayActionCallback]
  )

  const handleSkipMove = useCallback(() => {
    void makeGameplayActionCallback(null, "SKIP")
  }, [makeGameplayActionCallback])

  const isMyMatch = useMemo(() => {
    if (!playerProfile || !gameState) return false

    return Object.keys(gameState.players).includes(playerProfile._id)
  }, [playerProfile, gameState])

  const isMe = (playerIdentic?: string, currentPlayerId?: string) => {
    if (!playerProfile || !playerIdentic) return false

    const playerId = isMyMatch ? playerProfile._id : player?.player

    if (currentPlayerId) {
      const isMyPlayer = currentPlayerId === playerId

      if (playerIdentic === EffectTarget.OPPONENT) {
        return isMyPlayer ? false : true
      }

      return isMyPlayer
    }

    if (playerIdentic === EffectTarget.SELF) {
      return true
    }

    return playerIdentic === playerId
  }

  const player = useMemo(() => {
    if (!playerProfile || !gameState) return null

    if (!isMyMatch) {
      return gameState.players[matchSession?.winner || ""]
    }

    return gameState.players[playerProfile._id]
  }, [gameState, playerProfile, isMyMatch, matchSession])

  const opponent = useMemo(() => {
    if (!playerProfile || !gameState) return null

    if (!isMyMatch) {
      return gameState.players[
        matchSession?.winner === matchSession?.player1
          ? matchSession?.player2 || ""
          : matchSession?.player1 || ""
      ]
    }

    return Array.from(Object.values(gameState.players)).find(
      (player) => player.player !== playerProfile._id
    )
  }, [gameState, playerProfile, isMyMatch, matchSession])

  const { data: playerSkin } = useGetCardBackActiveByPlayerIdQuery(
    player?.player || "",
    {
      skip: !player?.player
    }
  )

  const { data: opponentSkin } = useGetCardBackActiveByPlayerIdQuery(
    opponent?.player || "",
    {
      skip: !opponent?.player
    }
  )

  const isMyTurn = useMemo(() => {
    if (!playerProfile || !gameState) return false

    return gameState.currentPlayerId === playerProfile._id
  }, [gameState, playerProfile])

  const lastEvent = useMemo(() => {
    return events[events.length - 1]
  }, [events])

  const playerActiveEffects = useMemo(() => {
    if (!activeEffects) return []

    return activeEffects.filter((effect) => isMe(effect.targetPlayer))
  }, [activeEffects, playerProfile])

  const opponentActiveEffects = useMemo(() => {
    if (!activeEffects) return []

    return activeEffects.filter((effect) => !isMe(effect.targetPlayer))
  }, [activeEffects, playerProfile])

  const playerCards = useMemo(() => {
    if (!cards) return []

    return cards.filter(
      (card) =>
        isMe(card.player) &&
        !(
          gameState?.currentCard?._id === card.card?._id &&
          gameState?.currentPlayerId === card.player
        )
    )
  }, [cards, playerProfile, gameState])

  const opponentCards = useMemo(() => {
    if (!cards) return []

    return cards.filter(
      (card) =>
        !isMe(card.player) &&
        !(
          gameState?.currentCard?._id === card.card?._id &&
          gameState?.currentPlayerId === card.player
        )
    )
  }, [cards, playerProfile, gameState])

  const { data: playerInfo } = useGetPlayerInfoQuery(player?.player || "", {
    skip: !player?.player
  })
  const { data: playerStatistics } = useGetPlayerStatisticsQuery(
    player?.player || "",
    {
      skip: !player?.player
    }
  )
  const { data: opponentInfo } = useGetPlayerInfoQuery(opponent?.player || "", {
    skip: !opponent?.player
  })
  const { data: opponentStatistics } = useGetPlayerStatisticsQuery(
    opponent?.player || "",
    {
      skip: !opponent?.player
    }
  )

  const playerName = useMemo(() => {
    return playerInfo?.name.split(" ")[0] || ""
  }, [playerInfo])

  const opponentName = useMemo(() => {
    return opponentInfo?.name.split(" ")[0] || ""
  }, [opponentInfo])

  const isWinner = useMemo(() => {
    return checkWinGame()
  }, [matchSession, player])

  const getActiveEffectsByPlayerId = useCallback(
    (playerId: string) => {
      if (isMe(playerId)) {
        return playerActiveEffects
      }

      return opponentActiveEffects
    },
    [playerActiveEffects, opponentActiveEffects]
  )

  useEffect(() => {
    refGameState.current = gameState
  }, [gameState])

  useEffect(() => {
    refPlayer.current = player ?? undefined
  }, [player])

  useEffect(() => {
    refOpponent.current = opponent ?? undefined
  }, [opponent])

  useEffect(() => {
    refIsMyTurn.current = isMyTurn
  }, [isMyTurn])

  useEffect(() => {
    refIsMatchActive.current = isMatchActive
  }, [isMatchActive])

  useEffect(() => {
    dispatch(setSessionId(sessionId))
  }, [sessionId])

  useEffect(() => {
    dispatch(
      setGameDerivedState({
        isMatchActive,
        isMatchFinished,
        isMyTurn,
        isWinner,
        isTonMatch
      })
    )
  }, [isMatchActive, isMatchFinished, isMyTurn, isWinner, isTonMatch])

  useEffect(() => {
    if (!isMatchSessionLoading && !matchSession) {
      closeAllModals()
      navigate(ROUTES.HOME, { replace: true })
    }
  }, [matchSession, isMatchSessionLoading])

  return {
    player,
    opponent,
    playerCards,
    isPlayerCardsLoading,
    gameState,
    gamePrevState,
    isGameStateLoading,
    isMyTurn,
    refIsMyTurn,
    lastEvent,
    events,
    isEventsFulfilled,
    handleDiscardCard,
    handlePlayCard,
    handleSkipMove,
    isMe,
    activeEffects,
    isActiveEffectsLoading,
    playerActiveEffects,
    opponentActiveEffects,
    refGameState,
    refPlayer,
    refOpponent,
    sessionId,
    matchSession,
    lastLogs,
    isLastLogsLoading,
    playerInfo,
    opponentInfo,
    playerStatistics,
    opponentStatistics,
    getActiveEffectsByPlayerId,
    playerName,
    opponentName,
    isGameStateFetching,
    opponentCards,
    isMatchActive,
    refIsMatchActive,
    checkWinGame,
    isMyMatch,
    isWinner,
    isMatchPending,
    isTonMatch,
    isMatchFinished,
    activeCardBack,
    playerSkin,
    opponentSkin
  }
}
